"use strict";

const MultipleSelectListControlRenderer = require("./MultipleSelectListControlRenderer");
const InputSearchControl = require("@gluenetworks/input_search_control");
const _ = require("lodash");

/**
 * Enhanced renderer for FilterableMultipleSelectListControl
 * Adds a search/filter input above the select list
 *
 * @class FilterableMultipleSelectListControlRenderer
 * @extends {MultipleSelectListControlRenderer}
 */
class FilterableMultipleSelectListControlRenderer extends MultipleSelectListControlRenderer {
    constructor(model) {
        super(model);

        const originalListElem = this._rootElem;

        this._rootElem = document.createElement("filterable-select-list-control");
        this._rootElem.dataset.multiple = true;
        this._rootElem.component = model;

        if (originalListElem.classList.contains("icon-options")) {
            this._rootElem.classList.add("icon-options");
        }

        this._filterContainer = document.createElement("div");
        this._filterContainer.classList.add("filter-container");

        this._filterInput = new InputSearchControl({
            placeholder: model.placeholder || "Search...",
            autocomplete: "off",
            darkTheme: true,
        });

        this._filterContainer.appendChild(this._filterInput._renderer.rootElement);

        this._listContainer = originalListElem;
        this._listContainer.classList.add("list-container");

        this._rootElem.appendChild(this._filterContainer);
        this._rootElem.appendChild(this._listContainer);

        this._updateFilterVisibility();
        this._registerFilterListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerFilterListeners() {
        this._onFilterInputEvent = this._onFilterInputEvent.bind(this);
        this._onFilterKeyDownEvent = this._onFilterKeyDownEvent.bind(this);
        this._onFilterChange = this._onFilterChange.bind(this);
        this._onShowFilter = this._onShowFilter.bind(this);

        this._filterInput.on("UserChange", this._onFilterInputEvent);
        this._filterInput.on("UserKeyDown", this._onFilterKeyDownEvent);
        this._model.on("FilterChange", this._onFilterChange);
        this._model.on("ShowFilter", this._onShowFilter);
    }

    _unregisterFilterListeners() {
        if (this._filterInput) {
            this._filterInput.removeListener("UserChange", this._onFilterInputEvent);
            this._filterInput.removeListener("UserKeyDown", this._onFilterKeyDownEvent);
        }
        if (this._model) {
            this._model.removeListener("FilterChange", this._onFilterChange);
            this._model.removeListener("ShowFilter", this._onShowFilter);
        }
    }

    _shutdown() {
        this._unregisterFilterListeners();
        Promise.all([this._filterInput.shutdown()]).then(() => {
            this._filterInput = undefined;
            this._filterContainer = undefined;
            this._listContainer = undefined;
        });
        return super._shutdown();
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onFilterInputEvent(newValue) {
        this._model.setFilterValue(newValue);
    }

    _onFilterKeyDownEvent(eventData) {
        if (eventData && (eventData.key === "Escape" || eventData.keyCode === 27)) {
            this._clearFilter();
        }
    }

    _clearFilter() {
        this._filterInput.value = "";
        this._model.clearFilter();
        this._filterInput._renderer.focus();
    }

    _onFilterChange() {
        this._updateFilteredOptions();
    }

    _onShowFilter() {
        this._updateFilterVisibility();
    }

    _onOptions(newVal) {
        super._onOptions(newVal);
        this._updateFilteredOptions();
    }

    _onValue(newVal) {
        // Override to skip scroll on user interactions
        this._setValue(newVal, true);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _updateFilterVisibility() {
        if (!this._filterContainer) {
            return;
        }
        this._filterContainer.style.display = this._model.showFilter ? "flex" : "none";
    }

    _updateFilteredOptions() {
        this._setOptions(this._model.filteredOptions);
        this._setValue(this._model.value, true); // Skip scroll when filtering
    }

    _setOptions(newVal) {
        if (!this._listContainer) {
            return super._setOptions(newVal);
        }

        this._selections.forEach((selection) => {
            selection.removeEventListener("click", this._onClickEvent);
            selection.removeEventListener("mouseenter", this._onOptionMouseEnter);
        });
        this._selections = [];
        this._listContainer.innerHTML = "";

        newVal.forEach((option) => {
            let selection = document.createElement("list-option");
            selection.innerHTML = option.label;
            selection.value = option.value;
            if (option.disabled) {
                selection.setAttribute("disabled", true);
            }
            if (option.description) {
                selection.setAttribute("title", option.description);
            }
            this._setOptionIcon(option.value, selection);
            selection.addEventListener("click", this._onClickEvent);
            selection.addEventListener("mouseenter", this._onOptionMouseEnter);
            this._listContainer.appendChild(selection);
            this._selections.push(selection);
        });
    }

    _setValue(newVal, skipScroll = false) {
        this._selections.forEach((selection) => {
            selection.removeAttribute("data-active");
            if (selection._glIconElem) {
                selection._glIconElem.dataset.icon = this._model.getOptionIcon(selection.value);
            }
        });

        let lastSelection = null;
        newVal.forEach((val) => {
            this._selections.forEach((selection) => {
                if (_.isEqual(selection.value, val)) {
                    selection.dataset.active = true;
                    if (selection._glIconElem) {
                        selection._glIconElem.dataset.icon = this._model.getSelectedOptionIcon(
                            selection.value
                        );
                    }
                    lastSelection = selection;
                }
            });
        });

        // Only scroll to the last selected item, and only if not skipped
        if (!skipScroll && lastSelection) {
            this._adjustSelectionFocusPosition(lastSelection);
        }
    }

    render() {
        return new Promise((resolve) => {
            this._updateFilteredOptions();
            this._setDisabled(this._model.disabled);
            this._setValue(this._model.value, true); // Skip scroll on initial render
            this._setVisible(this._model.visible);
            this._setInvalid(this._model.invalid);
            this._setDarkTheme(this._model.darkTheme);
            this._setTheme(this._model.theme);
            this._updateFilterVisibility();
            resolve();
        });
    }

    getRootElement() {
        return this._rootElem;
    }

    _adjustSelectionFocusPosition(selection) {
        const scrollTop = this._listContainer.scrollTop;
        const offsetHeight = this._listContainer.offsetHeight;
        const selectionTop = selection.offsetTop;
        const selectionBottom = selectionTop + selection.offsetHeight;

        // Only scroll if item is outside visible area
        if (selectionBottom > scrollTop + offsetHeight) {
            // Item is below visible area - scroll down
            this._listContainer.scrollTop = selectionBottom - offsetHeight;
        } else if (selectionTop < scrollTop) {
            // Item is above visible area - scroll up
            this._listContainer.scrollTop = selectionTop;
        }
        // If item is already visible, don't scroll
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = FilterableMultipleSelectListControlRenderer;
